import { Box, styled, useMediaQuery, useTheme } from '@mui/material';
import { orderBy } from 'lodash';
import { memo, useEffect, useMemo, useRef, useState } from 'react';

import { ActiveCase } from '#entities';
import { EAircraftEventStatus, ELayoutManagerMode, useManifest } from '#shared';

import { AircraftDrawer } from '../../AircraftDrawer';
import { Carousel, CarouselStatic } from '../../Carousel';
import {
  useAircraftSelection,
  useAirFleetCardLayout,
} from '../../data-context';
import { getCurrentEventLegacy, setCasesToAircrafts } from '../../helpers';
import { useLayoutMode } from '../../LayoutManager';
import {
  AIRFLEET_CARD_MAX_WIDTH,
  AIRFLEET_CARD_MIN_HEIGHT,
  AIRFLEET_CARD_MIN_WIDTH,
  AIRFLEET_CARD_MOBILE_MIN_HEIGHT,
  WIDGET_AIRFLEET_CARD_MAX_WIDTH,
  WIDGET_AIRFLEET_CARD_MIN_HEIGHT,
  WIDGET_AIRFLEET_CARD_MIN_HEIGHT_2K,
  WIDGET_AIRFLEET_CARD_MIN_HEIGHT_4K,
  WIDGET_AIRFLEET_CARD_MIN_WIDTH,
} from '../model';

import { AircraftCardAdapter } from './AircraftCardAdapter';

import type { AircraftCardItem, AircraftStatus } from '#entities';

const getAircraftCardSizes = (
  renderInsideWidget: boolean,
  isMobile: boolean,
  is2K: boolean,
  is4K: boolean,
) => {
  if (renderInsideWidget || isMobile) {
    const minHeight = is4K
      ? WIDGET_AIRFLEET_CARD_MIN_HEIGHT_4K
      : is2K
        ? WIDGET_AIRFLEET_CARD_MIN_HEIGHT_2K
        : WIDGET_AIRFLEET_CARD_MIN_HEIGHT;
    return {
      minHeight,
      minWidth: WIDGET_AIRFLEET_CARD_MIN_WIDTH,
      maxWidth: WIDGET_AIRFLEET_CARD_MAX_WIDTH,
    };
  }
  return {
    minHeight: AIRFLEET_CARD_MIN_HEIGHT,
    minWidth: AIRFLEET_CARD_MIN_WIDTH,
    maxWidth: AIRFLEET_CARD_MAX_WIDTH,
  };
};

const VerticalCardList = styled(Box)(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(1),
  height: '100%',
  maxHeight: '100%', // ensure it won't grow beyond parent
  overflowY: 'auto', // vertical scrolling here only
  overflowX: 'hidden',
  padding: theme.spacing(0, 1),
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',

  '&::-webkit-scrollbar': {
    display: 'none',
  },
}));

const PageShell = styled(Box, {
  shouldForwardProp: p => p !== 'insideWidget',
})<{ insideWidget?: boolean }>(({ theme, insideWidget = false }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  padding: insideWidget ? 0 : theme.spacing(1, 0),
  height: '100%',

  [theme.breakpoints.down('md')]: { '&:before, &:after': { display: 'none' } },
  [theme.breakpoints.up('md')]: insideWidget
    ? {}
    : { height: '100%', flex: '1 1 auto', minHeight: 0 },
}));

interface AircraftCardsPageContentProps {
  aircraftsList: AircraftStatus[];
  activeCases: ActiveCase[];
  backendNetworkError?: string | null;
  renderInsideWidget?: boolean;
  maxRows?: number;
  loopInterval?: number; // keep it configurable, shared across all carousels via carouselSync
}

export const AircraftCards = memo(
  ({
    aircraftsList,
    activeCases,
    backendNetworkError,
    renderInsideWidget = false,
    maxRows = 0,
    loopInterval = 1000,
  }: AircraftCardsPageContentProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const is2K = useMediaQuery(theme.breakpoints.up('2k'));
    const is4K = useMediaQuery(theme.breakpoints.up('4k'));
    const { mode } = useLayoutMode();
    const isPreviewMode = mode !== ELayoutManagerMode.RENDER;

    const shouldRenderVertically = !renderInsideWidget && isMobile;

    useManifest();

    const aircrafts = useMemo<AircraftCardItem[]>(() => {
      if (backendNetworkError) {
        return [];
      }
      return setCasesToAircrafts(aircraftsList, activeCases);
    }, [aircraftsList, activeCases, backendNetworkError]);

    const aircraftsSorted = useMemo(() => {
      const buckets = aircrafts.reduce<AircraftCardItem[][]>(
        (
          [
            available,
            onTrip,
            offDuty,
            unavailable,
            cleaning,
            scheduledMaint,
            aog,
          ],
          item,
        ) => {
          const currentEvent = getCurrentEventLegacy(item.Events);
          switch (currentEvent.Type) {
            case EAircraftEventStatus.OnDuty:
              available.push(item);
              break;
            case EAircraftEventStatus.OnTrip:
              onTrip.push(item);
              break;
            case EAircraftEventStatus.OffDuty:
              offDuty.push(item);
              break;
            case EAircraftEventStatus.NotAvailable:
              unavailable.push(item);
              break;
            case EAircraftEventStatus.OnMaintenance:
              if (currentEvent.MaintenanceType === 'AOG') {
                aog.push(item);
              } else if (currentEvent.MaintenanceType === 'CLN') {
                cleaning.push(item);
              } else {
                scheduledMaint.push(item);
              }
              break;
          }
          return [
            available,
            onTrip,
            offDuty,
            unavailable,
            cleaning,
            scheduledMaint,
            aog,
          ];
        },
        [[], [], [], [], [], [], []],
      );

      const [
        available,
        onTrip,
        offDuty,
        notAvailable,
        cleaning,
        scheduledMaint,
        aog,
      ] = buckets;

      // Available: more time on duty -> higher card
      const availableSorted = orderBy(
        available,
        it => {
          const ev = getCurrentEventLegacy(it.Events);
          // Use raw ms for finer ranking; falls back to 0 if missing.
          return ev?.EventTimeMs ?? 0;
        },
        ['desc'],
      );

      return ([] as AircraftCardItem[]).concat(
        availableSorted, // Available
        onTrip, // On Case
        offDuty.concat(notAvailable), // Unavailable (Off duty + NotAvailable)
        scheduledMaint.concat(cleaning), // Maintenance (Scheduled + Cleaning)
        aog, // AOG
      );
    }, [aircrafts]);

    const {
      open: isAircraftDrawerOpen,
      selectedAircraft,
      handleClose: handleAircraftDrawerClose,
      handleSelectAircraft,
    } = useAircraftSelection();

    const { minHeight, minWidth, maxWidth } = useMemo(
      () => getAircraftCardSizes(renderInsideWidget, isMobile, is2K, is4K),
      [renderInsideWidget, isMobile, is2K, is4K],
    );

    // compute rows + card width; reuses your existing math
    const { rows, colWidth, refCallback } = useAirFleetCardLayout({
      itemsCount: aircraftsSorted.length,
      minColWidth: minWidth,
      maxColWidth: maxWidth,
      minRowHeight: minHeight,
      maxRows: maxRows ?? 0,
      gap: 8,
    });
    const optimalColumnWidth = useMemo(() => {
      if (!containerWidth) {
        return WIDGET_AIRFLEET_CARD_MIN_WIDTH;
      }

      const maxColumns = Math.floor(
        containerWidth / WIDGET_AIRFLEET_CARD_MIN_WIDTH,
      );
      return Math.floor(containerWidth / maxColumns);
    }, [containerWidth]);

    useEffect(() => {
      if (!containerRef.current || !shouldRenderVertically) {
        return;
      }

      const observer = new ResizeObserver(entries => {
        const width = entries[0]?.contentRect.width ?? 0;
        setContainerWidth(width);
      });

      observer.observe(containerRef.current);
      return () => observer.disconnect();
    }, [shouldRenderVertically]);

    if (!aircraftsSorted.length || rows <= 0) {
      return null;
    }

    const itemProps = {
      width: colWidth,
      renderInsideWidget,
      onClickId: handleSelectAircraft,
    };

    const CarouselComponent = isPreviewMode ? CarouselStatic : Carousel;

    return (
      <PageShell insideWidget={renderInsideWidget}>
        <Box
          ref={refCallback}
          sx={{
            height: '100%',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            ['--carousel-row-min']: `${minHeight}px`,
            ['--card-min-h']: `${minHeight}px`,
          }}
        >
          {shouldRenderVertically ? (
            <VerticalCardList
              ref={containerRef}
              sx={{
                gridTemplateColumns: `repeat(auto-fit, minmax(${optimalColumnWidth}px, 1fr))`,
                '& > *': {
                  height: AIRFLEET_CARD_MOBILE_MIN_HEIGHT,
                },
              }}
            >
              {aircraftsSorted.map(item => (
                <AircraftCardAdapter
                  key={item.Id}
                  data={item}
                  width="100%"
                  renderInsideWidget={true}
                  onClickId={handleSelectAircraft}
                />
              ))}
            </VerticalCardList>
          ) : (
            <CarouselComponent
              renderItem={data => (
                <AircraftCardAdapter data={data} {...itemProps} />
              )}
              items={aircraftsSorted}
              itemKey="Id"
              normalSize={rows}
              gap={8}
              itemWidth={colWidth}
              scrollStepSize={isPreviewMode ? undefined : 1}
              loopInterval={isPreviewMode ? undefined : loopInterval}
              sx={{ height: '100%' }}
              normalStretch={true}
              normalMinSize={minHeight}
            />
          )}
        </Box>
        <AircraftDrawer
          open={isAircraftDrawerOpen}
          onClose={handleAircraftDrawerClose}
          aircraftData={selectedAircraft}
        >
          <AircraftDrawer.AircraftEvents />
          <AircraftDrawer.Location />
          <AircraftDrawer.Timeline />
          <AircraftDrawer.Weather />
        </AircraftDrawer>
      </PageShell>
    );
  },
);

AircraftCards.displayName = 'AircraftCardsPageContent';
